import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { ClinicalDataLanguageNotice } from '@/components/ClinicalDataLanguageNotice';
import type { DocumentRewriteResponse } from '@/types/document-rewrite';

interface RewriteViewProps {
  rewrite: DocumentRewriteResponse | null;
  isFetching: boolean;
  isError: boolean;
  /** Label of the second tab ("Original" or "Radiologist's report"). */
  originalLabel: string;
  plainSubline: string;
  originalSubline: string;
  /** The document as recorded, rendered by the caller. */
  original: ReactNode;
}

type Tab = 'plain' | 'original';

/**
 * "Plain language" / original tabs for an open note or imaging report. The
 * plain tab shows the AI rewrite; while it loads, a skeleton; when the call
 * failed, the original text with a small unavailable line.
 */
export default function RewriteView({ rewrite, isFetching, isError, originalLabel, plainSubline, originalSubline, original }: RewriteViewProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('plain');

  const tabClass = (id: Tab) =>
    `px-3 py-2 text-sm ${tab === id ? 'border-b-2 border-primary text-primary font-medium' : 'text-text-secondary hover:text-text-primary'}`;

  const renderPlain = () => {
    if (rewrite) {
      return rewrite.plain.split(/\n\s*\n/).map((paragraph, index) => (
        <p key={index} className="text-sm leading-relaxed text-text-primary mb-3">{paragraph.trim()}</p>
      ));
    }
    if (isError && !isFetching) {
      return (
        <>
          <p className="text-xs text-text-secondary mb-3" data-testid="plain-unavailable">{t('records.plainUnavailable')}</p>
          {original}
        </>
      );
    }
    return (
      <div className="space-y-2" data-testid="plain-loading" aria-busy="true">
        <p className="text-xs text-text-secondary">{t('records.plainLoading')}</p>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    );
  };

  return (
    <div>
      <div className="flex border-b mb-3" role="tablist">
        <button role="tab" aria-selected={tab === 'plain'} className={tabClass('plain')} onClick={() => setTab('plain')} data-testid="tab-plain">
          {t('records.tabPlainLanguage')}
        </button>
        <button role="tab" aria-selected={tab === 'original'} className={tabClass('original')} onClick={() => setTab('original')} data-testid="tab-original">
          {originalLabel}
        </button>
      </div>
      <p className="text-sm text-text-secondary mb-3">{tab === 'plain' ? plainSubline : originalSubline}</p>
      {tab === 'original' && <ClinicalDataLanguageNotice />}
      <div data-testid={`rewrite-${tab}`}>{tab === 'plain' ? renderPlain() : original}</div>
    </div>
  );
}
